import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Briefcase, TrendingUp, Users, Shield, Euro, ArrowRight, Sparkles,
  CheckCircle2, Calculator,
} from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';

const API = process.env.REACT_APP_BACKEND_URL;

const LEGAL_OPTIONS = [
  { value: 'auto_entrepreneur', label: 'Auto-entrepreneur' },
  { value: 'eurl', label: 'EURL' },
  { value: 'sasu', label: 'SASU' },
  { value: 'particulier', label: 'Particulier' },
  { value: 'other', label: 'Autre' },
];

const BENEFITS = [
  {
    Icon: Euro,
    title: '10 % HT de commission',
    text: 'Sur chaque formation signée par un candidat que tu as présenté à Sellerie Académie.',
  },
  {
    Icon: TrendingUp,
    title: 'Activité passionnante',
    text: 'Tu rejoins un métier d\'avenir : transmission d\'un savoir-faire artisanal rare et recherché.',
  },
  {
    Icon: Shield,
    title: 'Cadre transparent',
    text: 'Suivi en temps réel de tes candidats, de leurs statuts et de tes commissions dans un dashboard dédié.',
  },
  {
    Icon: Users,
    title: 'Aucune limite',
    text: 'Tu peux présenter autant de candidats que tu veux, partout en France et à l\'étranger.',
  },
];

export default function DevenirApporteurPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLangPrefix();
  const { user } = useAuth();

  const [referrer, setReferrer] = useState(null);
  const [calcAmount, setCalcAmount] = useState(1500);
  const [calcCount, setCalcCount] = useState(5);

  const refCode = new URLSearchParams(location.search).get('ref');
  useEffect(() => {
    if (refCode) {
      axios.get(`${API}/api/affiliate/resolve/${refCode}`)
        .then((r) => setReferrer(r.data))
        .catch(() => {});
    }
  }, [refCode]);

  // Inline registration form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '',
    legal_status: 'auto_entrepreneur', company_name: '', siret: '', iban: '', bio: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/auth/register-apporteur`, form);
      // Auto-login: store the token + redirect to dashboard
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      toast.success('Bienvenue ! Ton espace apporteur est prêt.');
      window.location.assign(withLangPrefix('/apporteur', lang));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur à l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const monthly = (calcAmount * calcCount * 0.1);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="devenir-apporteur">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 bg-[#1F2A4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          {referrer && (
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-[#C9B896]/15 border border-[#C9B896]/30 rounded-full text-sm" data-testid="affiliate-referrer-banner">
              <Sparkles className="w-3.5 h-3.5 text-[#C9B896]" />
              Recommandé par <strong>{referrer.first_name} {referrer.last_name}</strong>
            </div>
          )}
          <h1 className="font-light text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-4xl">
            Développe une activité<br />
            <span className="font-serif italic text-[#C9B896]">passionnante & d'avenir.</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl font-light max-w-2xl mt-10 leading-relaxed">
            Rejoins le réseau des apporteurs d'affaires Sellerie Académie. Pour chaque candidat
            que tu présentes et qui signe sa formation, tu touches une commission de <strong className="text-[#C9B896]">10 % HT</strong>.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <Button onClick={() => setShowForm(true)} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-9 py-7 rounded-full text-sm uppercase tracking-[0.25em]" data-testid="cta-become-affiliate">
              Devenir apporteur <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="#calculator" className="text-sm uppercase tracking-[0.25em] text-white/85 border-b border-[#C9B896] pb-1 hover:gap-3 inline-flex items-baseline gap-2 transition-all">
              Calculer mes gains <span className="text-[#C9B896]">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 mb-16">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#1F2A4A]">
                Un métier qui transmet,<br />
                <span className="font-serif italic text-[#C9B896]">une rémunération à la hauteur.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-7 hover:border-[#C9B896] transition-colors" data-testid={`benefit-${i}`}>
                <div className="w-11 h-11 rounded-full bg-[#C9B896]/15 flex items-center justify-center mb-4">
                  <b.Icon className="w-5 h-5 text-[#1F2A4A]" strokeWidth={1.6} />
                </div>
                <h3 className="font-medium text-xl text-[#1F2A4A] mb-2">{b.title}</h3>
                <p className="text-[#4A5568] font-light leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-12 md:py-16 bg-[#1F2A4A] text-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
          <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-5 flex items-center gap-2">
            <Calculator className="w-3 h-3" />SIMULER MES GAINS
          </p>
          <h2 className="font-light text-3xl md:text-5xl leading-[1.05] tracking-tight mb-12">
            Combien je peux <span className="font-serif italic text-[#C9B896]">gagner</span> ?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.25em] mb-3 block">Prix moyen d'une formation</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="500"
                    max="6000"
                    step="100"
                    value={calcAmount}
                    onChange={(e) => setCalcAmount(parseInt(e.target.value, 10))}
                    className="flex-1 accent-[#C9B896]"
                    data-testid="calc-amount-slider"
                  />
                  <span className="text-2xl font-serif italic text-[#C9B896] w-28 text-right">{calcAmount} €</span>
                </div>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.25em] mb-3 block">Formations signées par mois</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={calcCount}
                    onChange={(e) => setCalcCount(parseInt(e.target.value, 10))}
                    className="flex-1 accent-[#C9B896]"
                    data-testid="calc-count-slider"
                  />
                  <span className="text-2xl font-serif italic text-[#C9B896] w-28 text-right">{calcCount}</span>
                </div>
              </div>
            </div>
            <div className="bg-[#172239] border border-[#C9B896]/20 rounded-xl p-8 text-center">
              <p className="font-serif italic text-6xl md:text-7xl text-white leading-none my-4" data-testid="calc-result">
                {monthly.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </p>
              <p className="text-white/55 text-sm">soit {(monthly * 12).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € sur 12 mois</p>
              <p className="text-white/40 text-[10px] mt-4 font-mono uppercase tracking-[0.25em]">10 % HT — commission standard</p>
            </div>
          </div>
        </div>
      </section>

      {/* FORM / FINAL CTA */}
      <section className="py-12 md:py-16" id="register-form">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!showForm && !user ? (
            <div className="text-center">
              <h2 className="font-light text-4xl md:text-5xl text-[#1F2A4A] leading-tight mb-6">
                Commence en <span className="font-serif italic text-[#C9B896]">moins de 2 minutes.</span>
              </h2>
              <p className="text-[#4A5568] text-lg font-light mb-10 max-w-xl mx-auto">
                Crée ton compte apporteur, reçois ton code partenaire unique et commence à présenter des candidats dès aujourd'hui.
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] px-9 py-7 rounded-full text-sm uppercase tracking-[0.25em]" data-testid="cta-show-form">
                Créer mon compte apporteur <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : showForm ? (
            <form onSubmit={handleRegister} className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-8 md:p-10 space-y-5" data-testid="affiliate-register-form">
              <h2 className="font-light text-3xl text-[#1F2A4A]">Créer mon <span className="font-serif italic text-[#C9B896]">compte apporteur</span></h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="affiliate-first-name" />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="affiliate-last-name" />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="affiliate-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mot de passe (min 6 caractères) *</Label>
                  <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="affiliate-password" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Statut juridique *</Label>
                  <Select value={form.legal_status} onValueChange={(v) => setForm({ ...form, legal_status: v })}>
                    <SelectTrigger data-testid="affiliate-legal"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEGAL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Société (optionnel)</Label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SIRET (optionnel)</Label>
                  <Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
                </div>
                <div>
                  <Label>IBAN (optionnel)</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Présente-toi en quelques mots</Label>
                <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              </div>
              <p className="text-xs text-[#4A5568]/70">
                En créant un compte, tu acceptes nos conditions générales d'apport d'affaires (commission de 10 % HT sur les formations signées).
              </p>
              <div className="flex justify-between gap-3 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
                <Button type="submit" disabled={submitting} className="bg-[#1F2A4A] text-white hover:bg-[#324166] px-8" data-testid="affiliate-submit">
                  {submitting ? 'Création…' : 'Créer mon compte'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          ) : (
            <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
              <p className="text-[#4A5568] mb-6">Tu es déjà connecté.</p>
              <Link to={withLangPrefix(user?.role === 'apporteur' ? '/apporteur' : '/mes-formations', lang)}>
                <Button className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full px-7">
                  Accéder à mon espace <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
